import { Directive, OnDestroy } from '@angular/core';
import { IMouseListener } from "../interfaces/IMouseListener";
import { BeNeo4jService } from "../services/be-neo4j.service";
import { SharedService } from "../services/shared.service";
import { CameraState, DataViewer, ScreenshotOptions } from "./DataViewer";
import { Camera } from "../clases/Camera";
import { CanvasHelper } from "../clases/CanvasHandler";
import { CANVAS_BACKGROUND_COLOR } from "../styles/Canvas";
import { updateConnections } from "../utils/ConnectionUtils";
import { Values } from "../clases/Values";
import { BooleanRef } from "../clases/BooleanRef";
import { IKeyListener } from "../interfaces/IKeyListener";
import { AbstractShape } from "./Shape";
import { Connection } from "../clases/Connection";
import { getShapeWithId } from "../utils/ContainerUtils";

@Directive()
export abstract class CanvasRenderer extends DataViewer implements IMouseListener, IKeyListener, OnDestroy {
  constructor(
    protected override beNeo4jService: BeNeo4jService,
    protected override sharedService: SharedService
  ) { super(beNeo4jService, sharedService); }

  canvas!: HTMLCanvasElement;

  ctx!: CanvasRenderingContext2D;
  camera!: Camera;
  canvasHelper!: CanvasHelper;
  isShapeHover: boolean = false;
  isConnectionHover: boolean = false;
  _containerElement!: HTMLElement;
  mouseCanvas: Values = new Values(0,0);
  mousePosition: Values = new Values(0,0);
  isCtrl: BooleanRef = new BooleanRef();
  private preservedCameraState: CameraState | null = null;
  private resizeObserver: ResizeObserver | null = null;

  setupCanvas() {
    this.updateCanvasSize();
    this.ctx = this.canvas.getContext('2d')!;
    this.camera = new Camera();
    this.currentFocus.resetList();
    this.camera.isPanning = false;
    this.canvasHelper = new CanvasHelper(this.camera, this);
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(this.updateCanvasSize);
    this.resizeObserver.observe(this._containerElement);
  }

  override getCameraState(): CameraState | null {
    if (!this.camera) return null;

    return {
      offsetX: this.camera.offsetX,
      offsetY: this.camera.offsetY,
      scale: this.camera.scale
    };
  }

  override preserveCameraState(cameraState: CameraState | null) {
    this.preservedCameraState = cameraState ? { ...cameraState } : null;
    this.restorePreservedCameraState();
  }

  private restorePreservedCameraState() {
    if (!this.camera || !this.preservedCameraState) return;

    this.camera.offsetX = this.preservedCameraState.offsetX;
    this.camera.offsetY = this.preservedCameraState.offsetY;
    this.camera.scale = this.preservedCameraState.scale;
    this.camera.isPanning = false;
    this.draw();
  }

  updateCanvasSize = () => {
    let container = this._containerElement;
    const width = container.clientWidth;
    const height = container.clientHeight;
    // A background Material tab may have no layout box; retain its Canvas size.
    if (!width || !height) return;
    if (this.canvas.width === width && this.canvas.height === height) return;
    this.canvas.width = width;
    this.canvas.height = height;
    this.canvasBounding.width = width;
    this.canvasBounding.height = height;
    this.draw();
  }

  private eventController = new AbortController();

  ngOnDestroy(): void {
    this.eventController.abort();
    this.resizeObserver?.disconnect();
  }

  protected bindEvents() {
    this.eventController.abort();
    this.eventController = new AbortController();
    const signal = this.eventController.signal;
    this.camera.bindEvents(this.canvas, () => this.draw(), signal);
    this.canvas.addEventListener('mousedown', e => this.onMouseDown(e), { signal });
    this.canvas.addEventListener('mousemove', e => this.onMouseMove(e), { signal });
    window.addEventListener('mouseup', e => { if (this.agentGestureActive) this.onMouseUp(e); }, { signal });
    this.canvas.addEventListener('dblclick', e => this.onDoubleClick(e), { signal });
    window.addEventListener('keydown', e => {
      if (this._appComponent?.getCurrentDataView() !== this) return;
      if (e.target instanceof Element && e.target.closest('input, textarea, select, [contenteditable="true"], agent-dock, app-agent-dock')) return;
      this.onKeyDown(e);
    }, { signal });
    window.addEventListener('keyup', e => this.onKeyUp(e), { signal });
  }

  onMouseDown(e: MouseEvent) {
    this.agentGestureActive = true;
    const { clientX, clientY } = e;
    this.mousePosition = new Values(clientX, clientY);
    if (this.canvasHelper.checkCameraPanning(e)) return;

    if (
      this.canvasHelper.checkSelectedConnection(
        e,
        this.connections,
        this.canvasItems,
        this.currentConnectionFocus,
        this.currentFocus,
        this.setConnections
      )
    ) return;

    if (
      this.canvasHelper.checkSelectedShape(
        e,
        this.connections,
        this.canvasItems,
        this.currentConnectionFocus,
        this.currentFocus,
        this.setCanvasItems
      )
    ) return;
  }

  setCanvasItems=(items: AbstractShape[])=>{
    this.canvasItems = items;
  }

  setConnections=(items: Connection[])=>{
    this.connections = items;
  }

  onMouseMove(e: MouseEvent) {
    const { clientX, clientY } = e;
    this.mousePosition = new Values(clientX, clientY);
    const { offsetX, offsetY } = e;
    const { x, y } = this.camera.toCanvasCoordinates(offsetX, offsetY);
    this.mouseCanvas = new Values(x, y);
    this.isShapeHover = this.canvasHelper.onHoverShape(e, this.canvasItems, this.hoveredShape);
    this.isConnectionHover = this.canvasHelper.onHoverConnection(e, this.connections, this.hoveredConnection);
    this.canvasHelper.paneCamera(e);
    this.canvasHelper.resizeOrMoveShape(e, this.canvasItems, this.currentFocus);
    this.draw();
  }


  onMouseUp(e: MouseEvent) {
    this.agentGestureActive = false;
    if(e.button === 2){
      this.canvasHelper.stopCameraPanning();
      this.canvasHelper.removeDraggingOrResizing(this.currentFocus);
      this.draw();
      return;
    }
    this.canvasHelper.removeDraggingOrResizing(this.currentFocus);
    this.checkFocus();
    this.canvasHelper.stopCameraPanning();
    this.draw();
  }

  checkFocus() {
    if (!this.isConnectionHover && !this.isShapeHover) {
      this.canvasHelper.removeFocus(
        this.currentFocus,
        this.currentConnectionFocus,
        this.connections,
        this.canvasItems
      );
    }
  }

  onDoubleClick(e: MouseEvent) {
    this.canvasHelper.toggleFill();
    this.draw();
  }

  onKeyUp(e: KeyboardEvent){
    switch (e.key) {
      case "Control":
        this.isCtrl.value = false;
        return;
    }
  }

  onKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case "Control":
        this.isCtrl.value = true;
        return;
    }

    let focusList = this.currentFocus.getShape();
    for (const focus of focusList) {
      if(!focus)return;
      switch (e.key) {
        case "ArrowLeft":
          focus.setPosition(focus.x - 1, focus.y);
          break;
        case "ArrowRight":
          focus.setPosition(focus.x + 1, focus.y);
          break;
        case "ArrowUp":
          focus.setPosition(focus.x, focus.y - 1);
          break;
        case "ArrowDown":
          focus.setPosition(focus.x, focus.y + 1);
          break;
        default:
          break;
      }
    }
    this.draw();
  }

  override draw() {
    if (!this.ctx) return;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.save();

    this.ctx.fillStyle = CANVAS_BACKGROUND_COLOR;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Apply camera transformations
    this.ctx.translate(this.camera.offsetX, this.camera.offsetY);
    this.ctx.scale(this.camera.scale, this.camera.scale);

    for (const items of this.canvasItems) {
      items.draw(this.ctx);
    }

    updateConnections(this.connections, this.canvasItems);

    for (const connection of this.connections) {
      connection.draw(this.ctx);
    }

    this.drawOnTop(this.ctx);

    this.ctx.restore();
    this.updateContainers();
    this.observeAgentCanvas();
  }

  protected drawOnTop(ctx: CanvasRenderingContext2D) {}

  updateScreen = () => {
    this.draw();
  }

  fitBoxesWithCamera = ()=>{
    this.camera.fitToBoxes(this.canvasItems, this.canvas);
    this.draw();
  }

  override async takeChildScreenshot(
    itemOrId: AbstractShape | string,
    options: ScreenshotOptions = {}
  ): Promise<void> {
    const target =
      typeof itemOrId === "string"
        ? getShapeWithId(itemOrId, this.canvasItems)
        : itemOrId;

    if (!target) {
      throw new Error("Canvas item not found.");
    }

    const padding = Math.max(0, options.padding ?? 24);
    const requestedScale = Math.max(1, options.scale ?? 4);
    const exportWidth = target.width + (padding * 2);
    const exportHeight = target.height + (padding * 2);

    if (exportWidth <= 0 || exportHeight <= 0) {
      throw new Error("Canvas item has invalid dimensions.");
    }

    const MAX_EXPORT_SIDE = 8192;
    const MAX_EXPORT_PIXELS = 32_000_000;
    const limitTo32Megapixels = options.limitTo32Megapixels ?? true;
    const maxScaleBySide = Math.min(
      MAX_EXPORT_SIDE / exportWidth,
      MAX_EXPORT_SIDE / exportHeight
    );
    const maxScaleByPixels = limitTo32Megapixels
      ? Math.sqrt(MAX_EXPORT_PIXELS / (exportWidth * exportHeight))
      : Infinity;
    const effectiveScale = Math.max(
      Math.min(requestedScale, maxScaleBySide, maxScaleByPixels),
      Number.EPSILON
    );
    const scaledWidth = Math.max(1, Math.floor(exportWidth * effectiveScale));
    const scaledHeight = Math.max(1, Math.floor(exportHeight * effectiveScale));
    const renderScale = Math.min(
      scaledWidth / exportWidth,
      scaledHeight / exportHeight
    );

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = scaledWidth;
    exportCanvas.height = scaledHeight;

    const exportCtx = exportCanvas.getContext("2d");
    if (!exportCtx) {
      throw new Error("Unable to create screenshot context.");
    }

    exportCtx.imageSmoothingEnabled = true;
    exportCtx.imageSmoothingQuality = "high";

    if (options.backgroundColor !== null) {
      exportCtx.fillStyle = options.backgroundColor ?? CANVAS_BACKGROUND_COLOR;
      exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    }

    const absolutePosition = this.getCanvasItemAbsolutePosition(target);
    updateConnections(this.connections, this.canvasItems);
    const relatedConnections = this.getConnectionsForCanvasItemSubtree(target);
    const focusState = new Map<AbstractShape, boolean>();
    this.setFocusStateRecursive(target, false, focusState);
    const connectionState = new Map<Connection, { isFocus: boolean; isHovered: boolean }>();
    this.setConnectionState(relatedConnections, connectionState);

    try {
      exportCtx.scale(renderScale, renderScale);
      exportCtx.translate(
        padding - absolutePosition.x,
        padding - absolutePosition.y
      );
      target.draw(exportCtx);
      for (const connection of relatedConnections) {
        connection.draw(exportCtx);
      }
      target.drawOnTop(exportCtx);
    } finally {
      focusState.forEach((isFocused, shape) => {
        shape.focus = isFocused;
      });
      connectionState.forEach((state, connection) => {
        connection.setFocus(state.isFocus);
        connection.setHover(state.isHovered);
      });
    }

    const blob = await new Promise<Blob>((resolve, reject) => {
      exportCanvas.toBlob((generatedBlob) => {
        if (generatedBlob) {
          resolve(generatedBlob);
          return;
        }
        reject(new Error("Unable to generate screenshot image."));
      }, "image/png");
    });

    const downloadUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    const safeName = (options.fileName || target.name || target.id || "canvas-item")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "-");

    downloadLink.href = downloadUrl;
    downloadLink.download = `${safeName || "canvas-item"}.png`;
    downloadLink.click();

    setTimeout(() => {
      URL.revokeObjectURL(downloadUrl);
    }, 0);
  }

  private getCanvasItemAbsolutePosition(item: AbstractShape): Values {
    const offsets = item.calculateAllOffsets(this.canvasItems);
    return new Values(item.x + offsets.x, item.y + offsets.y);
  }

  private setFocusStateRecursive(
    item: AbstractShape,
    focus: boolean,
    focusState: Map<AbstractShape, boolean>
  ) {
    focusState.set(item, item.focus);
    item.focus = focus;

    for (const child of item.children) {
      this.setFocusStateRecursive(child, focus, focusState);
    }
  }

  private getConnectionsForCanvasItemSubtree(target: AbstractShape): Connection[] {
    const itemIds = new Set<string>();
    this.collectCanvasItemIdsRecursive(target, itemIds);

    return this.connections.filter((connection) =>
      itemIds.has(connection.from) || itemIds.has(connection.to)
    );
  }

  private collectCanvasItemIdsRecursive(item: AbstractShape, itemIds: Set<string>) {
    itemIds.add(item.id);

    for (const child of item.children) {
      this.collectCanvasItemIdsRecursive(child, itemIds);
    }
  }

  private setConnectionState(
    connections: Connection[],
    connectionState: Map<Connection, { isFocus: boolean; isHovered: boolean }>
  ) {
    for (const connection of connections) {
      connectionState.set(connection, {
        isFocus: connection.getFocus(),
        isHovered: connection.getHover()
      });
      connection.setFocus(false);
      connection.setHover(false);
    }
  }

  protected override afterFetchingContent(): void {
    this.fitBoxesWithCamera();
    this.restorePreservedCameraState();
  }
}
